import React from 'react';
import type { ReactNodeCompat } from '@/types/react-compat';
import type { StreamMessage } from '@/hooks/useChatStream';
import { Badge } from '@/components/ui/badge';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import ChatForm from './ChatForm';
import MessageList from './MessageList';

type Message = StreamMessage;

interface ChatHeaderProps {
  title: string;
  loading: boolean;
  actions?: ReactNodeCompat;
}

function ChatHeader({ title, loading, actions }: ChatHeaderProps) {
  return (
    <div className="flex items-center justify-between mb-6">
      <h1 className="text-2xl font-semibold">{title}</h1>
      <div className="flex items-center gap-2">
        {actions}
        {loading && (
          <Badge variant="secondary" className="animate-pulse">
            Risposta in corso...
          </Badge>
        )}
      </div>
    </div>
  );
}

interface ChatInterfaceProps {
  title?: string;
  assistantLabel?: string;
  input: string;
  setInput: (value: string) => void;
  msgList: Message[];
  loading: boolean;
  error: string | null;
  streamingMsg: string;
  bottomRef: React.RefObject<HTMLDivElement | null>;
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  copiedId: number | null;
  handleCopy: (id: number, content: string) => void;
  handleSend: (e: React.FormEvent) => void;
  image: File | null;
  setImage: (file: File | null) => void;
  readOnly?: boolean;
  headerActions?: ReactNodeCompat;
  infoBanner?: ReactNodeCompat;
  allowAttachments?: boolean;
  heightClass?: string;
  bottomSection?: ReactNodeCompat;
}

export function ChatInterface({
  title,
  assistantLabel = 'EmoviBot',
  input,
  setInput,
  msgList,
  loading,
  error,
  streamingMsg,
  bottomRef,
  inputRef,
  copiedId,
  handleCopy,
  handleSend,
  image,
  setImage,
  readOnly = false,
  headerActions,
  infoBanner,
  allowAttachments = true,
  heightClass,
  bottomSection,
}: ChatInterfaceProps) {
  const listContainerRef = React.useRef<HTMLDivElement>(null);
  const [showScrollDown, setShowScrollDown] = React.useState(false);

  React.useEffect(() => {
    const el = listContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      const threshold = 64; // px dal fondo
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
      setShowScrollDown(!atBottom);
    };
    el.addEventListener('scroll', onScroll);
    // init
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToBottom = () => {
    listContainerRef.current?.scrollTo({
      top: listContainerRef.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  return (
    <div className={cn('bellazzio mx-auto flex flex-col w-full min-h-0 overflow-hidden', heightClass)}>
      {title && <ChatHeader title={title} loading={loading} actions={headerActions} />}
      {infoBanner && <div className="mb-4">{infoBanner}</div>}
      <div className="flex-1 min-h-0 flex flex-col gap-4 overflow-hidden">
        <div className="relative flex-1 min-h-0 flex flex-col overflow-hidden">
          <MessageList
            messages={msgList}
            streamingMsg={streamingMsg}
            copiedId={copiedId}
            onCopy={handleCopy}
            bottomRef={bottomRef}
            containerRef={listContainerRef}
            assistantLabel={assistantLabel}
            loading={loading}
          />
          {showScrollDown && (
            <button
              type="button"
              aria-label="Torna in fondo"
              className="absolute right-4 bottom-4 z-10 rounded-full bg-background/80 backdrop-blur shadow border p-2 hover:bg-background"
              onClick={scrollToBottom}
            >
              <ChevronDown className="w-5 h-5" />
            </button>
          )}
        </div>
        {bottomSection && <div className="mt-2">{bottomSection}</div>}
        {!readOnly && (
          <ChatForm
            input={input}
            setInput={setInput}
            loading={loading}
            onSend={handleSend}
            inputRef={inputRef}
            image={image}
            setImage={setImage}
            allowAttachments={allowAttachments}
          />
        )}
      </div>
      {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
    </div>
  );
}
